// ============================================================
// TMR Portal Admin — a registration refused for a CNPJ or subdomain someone already has
// ============================================================
import { ApiError } from '@tmr/api-client';
import { COMPANY_ERROR_CODES } from './contract';
import type { CompanyForm } from './form';

/** The wizard fields a registration conflict can be about — both on its first step. */
export type ConflictField = 'cnpj' | 'subdomain';

/**
 * A 409 the backend pinned to one field: its message, and the value the operator had typed when it came back.
 * Kept with the value so the message disappears the moment the field is changed, and stays while it is not.
 */
export interface FieldConflict {
  field: ConflictField;
  message: string;
  value: string;
}

/**
 * Which field a failed onboarding or approval is about, read from the error code — never from the text (`09`).
 * Null for anything else: the race between two submissions (`conflict`), a validation failure, a closed
 * application. Those keep the banner, because no single field is to blame.
 *
 * @param error What the mutation threw.
 */
export function conflictField(error: unknown): ConflictField | null {
  if (!(error instanceof ApiError) || !error.isConflict) return null;

  switch (error.code) {
    case COMPANY_ERROR_CODES.cnpjTaken:
    case COMPANY_ERROR_CODES.cnpjInLegacy:
      return 'cnpj';
    case COMPANY_ERROR_CODES.subdomainTaken:
      return 'subdomain';
    default:
      return null;
  }
}

/**
 * The conflict to show on a field, captured against the form as it was submitted.
 *
 * @param error What the mutation threw.
 * @param submitted The form that was sent.
 */
export function toFieldConflict(error: unknown, submitted: CompanyForm): FieldConflict | null {
  const field = conflictField(error);
  if (field === null || !(error instanceof ApiError)) return null;
  return { field, message: error.detail, value: submitted[field] };
}

/**
 * The field errors a conflict adds to the first step — the backend's own sentence, e.g. "Este CNPJ já existe no
 * legado como o produtor 685…" — for as long as the field still holds the value that was refused.
 *
 * @param conflict The last conflict, or null.
 * @param form The form as it is now.
 */
export function conflictErrors(
  conflict: FieldConflict | null,
  form: CompanyForm,
): Partial<Record<ConflictField, string>> {
  if (conflict === null || form[conflict.field] !== conflict.value) return {};
  return { [conflict.field]: conflict.message };
}
